import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";

// Hoisted: avoids creating a new array on every render of the preview pane.
const REMARK_PLUGINS = [remarkGfm];

const inputCls =
  "mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#1E3A8A]";

const empty = {
  type: "page",
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  cover_image: "",
  published: false,
};

export default function AdminContent() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/content", { params: { limit: 200 } });
    setItems(data.items || []);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/content/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const togglePublish = async (it) => {
    try {
      const { data } = await api.patch(`/admin/content/${it.id}`, { published: !it.published });
      setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, ...data } : x)));
      toast.success(data.published ? "Published" : "Unpublished");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = filter === "all" ? items : items.filter((x) => x.type === filter);

  return (
    <div data-testid="admin-content">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow text-[#1E3A8A]">— Content Studio</p>
          <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
            Content
          </h1>
          <p className="text-[#555] mt-3">Pages and Journal posts. Markdown supported.</p>
        </div>
        <Button
          data-testid="admin-new-content-btn"
          onClick={() => setEditing(empty)}
          className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-5 h-auto label-eyebrow"
        >
          + New
        </Button>
      </div>

      <div className="mt-8 flex gap-2 border-b border-[#E5E0D8]">
        {[
          { k: "all", l: "All" },
          { k: "page", l: "Pages" },
          { k: "post", l: "Journal" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`content-tab-${t.k}`}
            onClick={() => setFilter(t.k)}
            className={`label-eyebrow px-5 py-3 -mb-px border-b-2 transition-colors ${
              filter === t.k
                ? "border-[#1E3A8A] text-[#1E3A8A]"
                : "border-transparent text-[#555] hover:text-[#111]"
            }`}
          >
            {t.l} ({t.k === "all" ? items.length : items.filter((x) => x.type === t.k).length})
          </button>
        ))}
      </div>

      <div className="mt-8 border border-[#E5E0D8] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4EFE6]/40 border-b border-[#E5E0D8]">
            <tr>
              <th className="text-left p-3 label-eyebrow text-[#555]">Title</th>
              <th className="text-left p-3 label-eyebrow text-[#555]">Type</th>
              <th className="text-left p-3 label-eyebrow text-[#555]">Slug</th>
              <th className="text-left p-3 label-eyebrow text-[#555]">Status</th>
              <th className="text-left p-3 label-eyebrow text-[#555]">Updated</th>
              <th className="text-right p-3 label-eyebrow text-[#555]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} data-testid={`content-row-${it.id}`} className="border-b border-[#E5E0D8] last:border-b-0 hover:bg-[#F4EFE6]/30">
                <td className="p-3 font-serif-display text-base text-[#111]">{it.title}</td>
                <td className="p-3">
                  <span className="label-eyebrow bg-[#E5E0D8] text-[#555] px-2 py-1">{it.type}</span>
                </td>
                <td className="p-3 font-mono-aam text-xs text-[#555]">
                  /{it.type === "post" ? "journal" : "p"}/{it.slug}
                </td>
                <td className="p-3">
                  <button
                    onClick={() => togglePublish(it)}
                    data-testid={`toggle-publish-${it.id}`}
                    className={`label-eyebrow px-2 py-1 ${
                      it.published
                        ? "bg-[#1E3A8A] text-[#F4C430]"
                        : "bg-[#E5E0D8] text-[#555]"
                    }`}
                  >
                    {it.published ? "Published" : "Draft"}
                  </button>
                </td>
                <td className="p-3 font-mono-aam text-xs text-[#555]">
                  {it.updated_at?.slice(0, 10)}
                </td>
                <td className="p-3 text-right">
                  <button
                    onClick={async () => {
                      const { data } = await api.get(`/admin/content/${it.id}`);
                      setEditing(data);
                    }}
                    data-testid={`edit-content-${it.id}`}
                    className="label-eyebrow text-[#1E3A8A] hover:underline mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(it.id, it.title)}
                    data-testid={`delete-content-${it.id}`}
                    className="label-eyebrow text-[#D32F2F] hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[#555]">No content yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ContentEditor
        item={editing}
        onClose={() => setEditing(null)}
        onSaved={(c) => {
          setEditing(null);
          load();
        }}
      />
    </div>
  );
}

function ContentEditor({ item, onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        ...empty,
        ...item,
        excerpt: item.excerpt || "",
        cover_image: item.cover_image || "",
        body: item.body || "",
      });
      setPreview(false);
    }
  }, [item]);

  if (!item) return null;
  const isNew = !item.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const save = async () => {
    if (!form.title || !form.body) {
      toast.error("Title and body are required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...form };
        if (!payload.slug) delete payload.slug;
        await api.post("/admin/content", payload);
      } else {
        await api.patch(`/admin/content/${item.id}`, form);
      }
      toast.success(isNew ? "Created" : "Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="content-editor" className="rounded-none max-w-4xl border border-[#E5E0D8] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#111] tracking-tight">
            {isNew ? "New content" : `Edit · ${item.title}`}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="label-eyebrow text-[#555]">Type</label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="content-type" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="page">Page (static)</SelectItem>
                <SelectItem value="post">Journal post</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="label-eyebrow text-[#555]">Slug (URL)</label>
              <Input data-testid="content-slug" value={form.slug} onChange={set("slug")} placeholder="auto-generated from title" className={inputCls} />
            </div>
            <label className="label-eyebrow text-[#555] flex items-center gap-2 pb-3">
              <input type="checkbox" data-testid="content-published" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
              Published
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow text-[#555]">Title</label>
            <Input data-testid="content-title" value={form.title} onChange={set("title")} className={inputCls} />
          </div>
          {form.type === "post" && (
            <>
              <div className="md:col-span-2">
                <label className="label-eyebrow text-[#555]">Excerpt</label>
                <Input data-testid="content-excerpt" value={form.excerpt} onChange={set("excerpt")} placeholder="Short summary for listings" className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <label className="label-eyebrow text-[#555]">Cover image URL</label>
                <Input data-testid="content-cover" value={form.cover_image} onChange={set("cover_image")} placeholder="https://… or /api/files/{id}" className={inputCls} />
              </div>
            </>
          )}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between">
              <label className="label-eyebrow text-[#555]">Body (Markdown)</label>
              <button
                type="button"
                onClick={() => setPreview(!preview)}
                data-testid="content-preview-toggle"
                className="label-eyebrow text-[#1E3A8A] hover:underline"
              >
                {preview ? "Edit" : "Preview"}
              </button>
            </div>
            {preview ? (
              <div data-testid="content-preview" className="mt-2 border border-[#E5E0D8] bg-white p-6 min-h-[400px] prose prose-neutral max-w-none font-serif-display [&_h2]:font-medium [&_h2]:text-2xl [&_h2]:mt-6 [&_h2]:mb-3 [&_p]:font-sans [&_p]:text-[#333] [&_p]:my-3 [&_ul]:font-sans [&_li]:my-1 [&_strong]:font-semibold">
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{form.body || "_Nothing to preview_"}</ReactMarkdown>
              </div>
            ) : (
              <Textarea
                data-testid="content-body"
                rows={18}
                value={form.body}
                onChange={set("body")}
                placeholder="# Heading\n\nWrite in Markdown…"
                className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent font-mono-aam text-sm focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
              />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline" className="rounded-none border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white label-eyebrow">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="content-save-btn" className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow">
            {saving ? "Saving…" : isNew ? "Create" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
